import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../../Services/Supabase';
import { Container, Spinner } from 'react-bootstrap';

import { ALLERGENS } from '../../Components/MenuAllergens';
import './Main.css';

const CLOUDFLARE_WORKER_URL = 'https://img.jovanni-tidei.workers.dev/';

const Main = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { clientSubdomain } = useParams();
  const [activeSection, setActiveSection] = useState(null);

  const getThemeClass = (theme = 'minimale') => {
    const themeClasses = {
      elegant: 'theme-elegant',
      rustico: 'theme-rustico',
      minimale: 'theme-minimale',
      sofisticato: 'theme-sofisticato',
      alternativo: 'theme-alternativo',
      casereccio: 'theme-casereccio',
    };
    return themeClasses[theme] || themeClasses.minimale;
  };

  // Funzione per attivare/disattivare l'accordion con scroll automatico
  const toggleSection = (sectionId) => {
    if (activeSection === sectionId) {
      setActiveSection(null);
    } else {
      if (activeSection !== null) {
        setActiveSection(null);
        setTimeout(() => {
          setActiveSection(sectionId);
          const header = document.getElementById(`section-header-${sectionId}`);
          if (header) {
            header.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 350);
      } else {
        setActiveSection(sectionId);
        const header = document.getElementById(`section-header-${sectionId}`);
        if (header) {
          header.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      
      if (!clientSubdomain) {
        setError("Subdomain non specificato");
        setLoading(false);
        return;
      }
  
      try {
        const { data, error } = await supabase
          .from("menu")
          .select("*")
          .eq('client_subdomain', clientSubdomain)
          .single();
  
        if (error) {
          setError(error.message);
        } else if (data) {
          setMenu({
            ...data,
            sections: data.sections || []
          });
        } else {
          setError(`Nessun menu trovato per: ${clientSubdomain}`);
        }
      } catch (err) {
        console.error('Errore generale:', err);
        setError(err.message);
      }
  
      setLoading(false);
    };
  
    fetchMenu();
  }, [clientSubdomain]);

  // Aggiungi questo useEffect per il reload automatico
  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === "forceReloadMain") {
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center fullheight">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="text-center error-container">
        <div className="error-message">
          <h2>Errore</h2>
          <p className="text-danger">{error}</p>
        </div>
      </Container>
    );
  }

  if (!menu) {
    return (
      <Container className="text-center error-container">
        <div className="error-message">
          <h2>Menu non trovato</h2>
          <p>Nessun menu disponibile per questo ristorante.</p>
        </div>
      </Container>
    );
  }

  const themeClass = getThemeClass(menu.theme);

  return (
    <div className={`theme-container ${themeClass}`}>
      {menu.logo_url && (
        <div className="hero-banner">
          <img 
            src={menu.logo_url.includes('workers.dev') 
              ? menu.logo_url 
              : `${CLOUDFLARE_WORKER_URL}?url=${encodeURIComponent(menu.logo_url)}`
            }
            alt={menu.name} 
            className="hero-image"
          />
        </div>
      )}

      <Container fluid className="menu-container">
        <div className="menu-header">
          <h1 className="menu-title">{menu.name}</h1>
          {menu.subtitle && (
            <p className="menu-subtitle">{menu.subtitle}</p>
          )}
        </div>
  
        <div className="sections-container">
          {menu.sections.map((section) => (
            <div key={section.id} className="menu-section">
              <button 
                id={`section-header-${section.id}`}
                className={`section-header ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => toggleSection(section.id)}
              >
                <span>{section.name}</span>
                <span className="accordion-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </span>
              </button>
              
              <div className={`section-content ${activeSection === section.id ? 'active' : ''}`}>
                {section.items.map((item) => (
                  <div 
                    key={item.id} 
                    className="menu-item" 
                    style={{ 
                      border: 'none',
                      borderBottom: 'none',
                      marginBottom: 0,
                      paddingBottom: '0.5rem'
                    }}
                  >
                    <div className="item-header">
                      <h3 className="item-name">{item.name}</h3>
                      <span className="item-price">
                        {item.price ? `€${parseFloat(item.price).toFixed(2)}` : ''}
                      </span>
                    </div>
                    {item.description && (
                      <p className="item-description">{item.description}</p>
                    )}
                    {item.allergens && item.allergens.length > 0 && (
                      <div className="allergens-row">
                        {item.allergens.map((allergenKey) => {
                          const allergen = ALLERGENS[allergenKey];
                          if (!allergen) return null;
                          const Icon = allergen.icon;
                          return (
                            <div 
                              key={allergenKey} 
                              className="allergen-icon"
                              title={allergen.label}
                            >
                              <Icon 
                                width={20} 
                                height={20}
                                outerColor={allergen.color}
                                innerColor="#ffffff"
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

export default Main;
